// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).
// =============================================================================
// BannersAdmin — manage dashboard banners
// =============================================================================
// Renders: CRUD list of banner rows (text, severity colour, active flag).
//          Stored in the `banners` table (renamed from `announcements` in
//          Phase 2). Surfaced read-only by ActiveBanners.
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { insertBanner, listAllBanners, updateBannerById } from "../../data/banners";
import {
  BANNER_AUDIENCES,
  bannerAudienceLabels,
  bannerAudiences,
} from "../../services/bannerAudience";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

export function BannersAdmin() {
  const [banners, setBanners] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({
    message: "",
    audience: "all",
    states: [],
    style: "info",
    active: true,
    expires_at: "",
  });
  const [saving, setSaving] = React.useState(false);

  const STATES = ["QLD", "NSW", "VIC", "WA", "SA", "TAS", "ACT", "NT"];
  const STYLES = [
    { val: "info", label: "Info (blue)", bg: "#E3F2FD", border: "#90CAF9", text: "#1565C0" },
    { val: "warning", label: "Warning (amber)", bg: "#FFF8E1", border: "#FFD54F", text: "#5D4037" },
    { val: "urgent", label: "Urgent (red)", bg: "#FFEBEE", border: "#EF9A9A", text: "#C62828" },
    { val: "success", label: "Success (green)", bg: "#E8F5E9", border: "#A5D6A7", text: "#2E7D32" },
  ];

  async function load() {
    setLoading(true);
    var rows = await listAllBanners();
    if (rows) setBanners(rows);
    setLoading(false);
  }

  React.useEffect(function () {
    load();
  }, []);

  function startNew() {
    setEditing("new");
    setForm({
      message: "",
      audience: ["all"],
      states: [],
      style: "info",
      active: true,
      expires_at: "",
    });
  }

  function startEdit(b) {
    setEditing(b.id);
    setForm({
      message: b.message || "",
      audience: bannerAudiences(b.audience).length ? bannerAudiences(b.audience) : ["all"],
      states: b.states || [],
      style: b.style || "info",
      active: b.active !== false,
      expires_at: b.expires_at || "",
    });
  }

  async function save() {
    if (!form.message.trim()) return;
    setSaving(true);
    var row = {
      message: form.message,
      audience: form.audience,
      states: form.states,
      style: form.style,
      active: form.active,
      expires_at: form.expires_at || null,
    };
    if (editing === "new") {
      await insertBanner(row);
    } else {
      await updateBannerById(editing, row);
    }
    setSaving(false);
    setEditing(null);
    load();
  }

  async function toggleActive(b) {
    await updateBannerById(b.id, { active: !b.active });
    load();
  }

  function toggleState(s) {
    var arr = form.states || [];
    setForm(function (prev) {
      return Object.assign({}, prev, {
        states: arr.includes(s)
          ? arr.filter(function (x) {
              return x !== s;
            })
          : [...arr, s],
      });
    });
  }

  function toggleAudience(v) {
    const arr = form.audience || [];
    setForm(function (prev) {
      return Object.assign({}, prev, {
        audience: arr.includes(v)
          ? arr.filter(function (x) {
              return x !== v;
            })
          : [...arr, v],
      });
    });
  }

  var styleMap = {};
  STYLES.forEach(function (s) {
    styleMap[s.val] = s;
  });

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="m-0 text-[18px] text-zephyr-text">Banners &amp; Announcements</h2>
        <Btn onClick={startNew}>+ New Banner</Btn>
      </div>
      {loading ? (
        <p className="text-zephyr-muted">Loading...</p>
      ) : banners.length === 0 ? (
        <p className="text-zephyr-muted text-sm">No banners created yet.</p>
      ) : (
        <div>
          {banners.map(function (b) {
            var st = styleMap[b.style] || styleMap.info;
            return (
              <div
                key={b.id}
                className={[
                  "border border-zephyr-border rounded-[10px] mb-[10px] overflow-hidden",
                  b.active ? "" : "opacity-60",
                ].join(" ")}
              >
                <div
                  className="py-3 px-4 flex justify-between items-start gap-3"
                  style={{ background: st.bg, borderLeft: `4px solid ${st.border}` }}
                >
                  <div className="flex-1">
                    <p
                      className="m-0 mb-[6px] text-[14px] font-semibold whitespace-pre-wrap"
                      style={{ color: st.text }}
                    >
                      {b.message}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <span
                        className="text-[11px] bg-[rgba(0,0,0,0.08)] rounded-[4px] py-[2px] px-[7px]"
                        style={{ color: st.text }}
                      >
                        {bannerAudienceLabels(b.audience)}
                      </span>
                      {b.states && b.states.length > 0 && (
                        <span
                          className="text-[11px] bg-[rgba(0,0,0,0.08)] rounded-[4px] py-[2px] px-[7px]"
                          style={{ color: st.text }}
                        >
                          {b.states.join(", ")}
                        </span>
                      )}
                      {b.expires_at && (
                        <span
                          className="text-[11px] bg-[rgba(0,0,0,0.08)] rounded-[4px] py-[2px] px-[7px]"
                          style={{ color: st.text }}
                        >
                          Expires {b.expires_at}
                        </span>
                      )}
                      <span
                        className="text-[11px] rounded-[4px] py-[2px] px-[7px]"
                        style={{
                          background: b.active ? "#E8F5E9" : "#EEEEEE",
                          color: b.active ? "#2E7D32" : "#999",
                        }}
                      >
                        {b.active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-[6px] shrink-0">
                    <button
                      onClick={function () {
                        toggleActive(b);
                      }}
                      className="bg-transparent border border-zephyr-border rounded-[6px] py-1 px-[10px] text-xs cursor-pointer text-zephyr-muted"
                    >
                      {b.active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      onClick={function () {
                        startEdit(b);
                      }}
                      className="bg-transparent border border-zephyr-teal rounded-[6px] py-1 px-[10px] text-xs cursor-pointer text-zephyr-teal"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <ModalBackdrop
          onClose={function () {
            setEditing(null);
          }}
          contentStyle={{
            background: "white",
            borderRadius: 12,
            padding: 28,
            maxWidth: 540,
            width: "calc(100vw - 40px)",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <h3 className="m-0 mb-5 text-zephyr-teal">
            {editing === "new" ? "New Banner" : "Edit Banner"}
          </h3>
          <div className="mb-[14px]">
            <label className="text-[13px] font-bold text-zephyr-text block mb-[6px]">Message</label>
            <textarea
              value={form.message}
              onChange={function (e) {
                setForm(function (p) {
                  return Object.assign({}, p, { message: e.target.value });
                });
              }}
              className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[14px] w-full box-border"
              style={{ minHeight: 80, fontFamily: "inherit" }}
              placeholder="Banner message..."
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-[14px]">
            <div>
              <label className="text-[13px] font-bold text-zephyr-text block mb-[6px]">
                Audience{" "}
                <span className="font-normal text-zephyr-muted text-[11px]">
                  (tick all that apply)
                </span>
              </label>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {BANNER_AUDIENCES.map(function (o) {
                  return (
                    <label
                      key={o.value}
                      className="flex items-center gap-1 text-[13px] cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={(form.audience || []).includes(o.value)}
                        onChange={function () {
                          toggleAudience(o.value);
                        }}
                      />
                      {o.label}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-[13px] font-bold text-zephyr-text block mb-[6px]">Style</label>
              <select
                value={form.style}
                onChange={function (e) {
                  setForm(function (p) {
                    return Object.assign({}, p, { style: e.target.value });
                  });
                }}
                className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[14px] w-full box-border"
                style={{ fontFamily: "inherit" }}
              >
                {STYLES.map(function (s) {
                  return (
                    <option key={s.val} value={s.val}>
                      {s.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          <div className="mb-[14px]">
            <label className="text-[13px] font-bold text-zephyr-text block mb-[6px]">
              States (leave blank for all)
            </label>
            <div className="flex gap-2 flex-wrap">
              {STATES.map(function (s) {
                var sel = (form.states || []).includes(s);
                return (
                  <button
                    key={s}
                    onClick={function () {
                      toggleState(s);
                    }}
                    className={[
                      "py-1 px-3 rounded-[6px] border text-[13px] cursor-pointer",
                      sel
                        ? "border-zephyr-teal bg-zephyr-teal-light text-zephyr-teal"
                        : "border-zephyr-border bg-white text-zephyr-text",
                    ].join(" ")}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label className="text-[13px] font-bold text-zephyr-text block mb-[6px]">
                Expires (optional)
              </label>
              <input
                type="date"
                value={form.expires_at}
                onChange={function (e) {
                  setForm(function (p) {
                    return Object.assign({}, p, { expires_at: e.target.value });
                  });
                }}
                className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[14px] w-full box-border"
                style={{ fontFamily: "inherit" }}
              />
            </div>
            <div className="flex items-end pb-[2px]">
              <label className="flex items-center gap-2 text-[14px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={function (e) {
                    setForm(function (p) {
                      return Object.assign({}, p, { active: e.target.checked });
                    });
                  }}
                />
                Active (visible to users)
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Btn onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save Banner"}
            </Btn>
            <Btn
              variant="secondary"
              onClick={function () {
                setEditing(null);
              }}
            >
              Cancel
            </Btn>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
